import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, Download, ChevronUp, ChevronDown, FileText,
  Heading as HeadingIcon, AlignLeft, LayoutGrid, TrendingUp, Zap,
  AlertTriangle, Wrench, Activity, Minus, FolderOpen, PenLine, ShieldCheck,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { API_BASE } from '../../utils/api-base';

interface ReportBuilderProps {
  clientId: string;
  token: string;
}

type BlockType =
  | 'heading' | 'text' | 'divider' | 'kpi-row'
  | 'trend-table' | 'energy-summary' | 'alarm-summary'
  | 'work-order-summary' | 'asset-health';

interface Block {
  id: string;
  type: BlockType;
  config: any;
}

interface ReportDef {
  id: string;
  name: string;
  blocks: Block[];
  updatedAt?: string;
}

interface PlcTag {
  id: string;
  plcId: string;
  plcName: string;
  name: string;
}

interface ReportSignature {
  id: string;
  signedByName: string | null;
  signedByEmail: string | null;
  signedByRole: string | null;
  reason: string;
  meaning: string;
  periodStart: string;
  periodEnd: string;
  signedAt: string;
}

const SIGNATURE_MEANINGS = ['Approval', 'Review', 'Verification', 'Release'];

const BLOCK_TYPES: { type: BlockType; label: string; icon: any; description: string }[] = [
  { type: 'heading', label: 'Heading', icon: HeadingIcon, description: 'Section title text' },
  { type: 'text', label: 'Text', icon: AlignLeft, description: 'Free-form paragraph' },
  { type: 'kpi-row', label: 'KPI Tiles', icon: LayoutGrid, description: 'Row of real stat tiles' },
  { type: 'trend-table', label: 'Trend Data', icon: TrendingUp, description: 'Historical tag values, aggregated by period' },
  { type: 'energy-summary', label: 'Energy Summary', icon: Zap, description: 'Meter avg / min / max table' },
  { type: 'alarm-summary', label: 'Alarm History', icon: AlertTriangle, description: 'Alarms triggered in the period' },
  { type: 'work-order-summary', label: 'Work Orders', icon: Wrench, description: 'Maintenance work orders' },
  { type: 'asset-health', label: 'Asset Health', icon: Activity, description: 'Online/offline snapshot' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal line spacer' },
];

const KPI_METRIC_OPTIONS: { key: string; label: string }[] = [
  { key: 'activeAlarms', label: 'Active Alarms' },
  { key: 'workOrdersOpen', label: 'Open Work Orders' },
  { key: 'workOrdersCompleted', label: 'Completed Work Orders' },
  { key: 'ticketsCreated', label: 'Tickets Created' },
  { key: 'logins', label: 'User Logins' },
  { key: 'assetsOnlinePct', label: 'Assets Online %' },
];

const newBlockId = () => `blk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const defaultConfigFor = (type: BlockType): any => {
  if (type === 'heading') return { text: 'Section Title' };
  if (type === 'text') return { text: '' };
  if (type === 'kpi-row') return { metrics: ['activeAlarms', 'workOrdersOpen'] };
  if (type === 'trend-table') return { plcId: '', tagIds: [], title: 'Trend Data' };
  return {};
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthAgoIso = () => new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

export function ReportBuilder({ clientId, token }: ReportBuilderProps) {
  const [reports, setReports] = useState<ReportDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState<ReportDef | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [plcTags, setPlcTags] = useState<PlcTag[]>([]);
  const [fromDate, setFromDate] = useState(monthAgoIso());
  const [toDate, setToDate] = useState(todayIso());
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signPassword, setSignPassword] = useState('');
  const [signReason, setSignReason] = useState('');
  const [signMeaning, setSignMeaning] = useState('Approval');
  const [signing, setSigning] = useState(false);
  const [signatures, setSignatures] = useState<ReportSignature[]>([]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadReports = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/custom-reports`, { headers: authHeaders });
      const payload = await res.json();
      if (res.ok && payload?.success) setReports(payload.reports || []);
    } catch (_) { /* keep last known state */ }
    finally { setLoading(false); }
  };

  const loadSignatures = async (reportId: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/custom-reports/${reportId}/signatures`, { headers: authHeaders });
      const payload = await res.json();
      if (res.ok && payload?.success) setSignatures(payload.signatures || []);
    } catch (_) { /* signed-versions list just stays empty */ }
  };

  const loadPlcTags = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/latest-values`, { headers: authHeaders });
      const payload = await res.json();
      if (res.ok && payload?.success) setPlcTags(payload.tags || []);
    } catch (_) { /* trend block config just won't have options yet */ }
  };

  useEffect(() => {
    if (clientId && token) { loadReports(); loadPlcTags(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const plcOptions = useMemo(() => {
    const map = new Map<string, string>();
    plcTags.forEach((t) => map.set(t.plcId, t.plcName));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [plcTags]);

  const tagsForPlc = (plcId: string) => plcTags.filter((t) => t.plcId === plcId);

  const createNewReport = () => {
    setActiveReport({ id: '', name: 'New Report', blocks: [] });
    setSignatures([]);
  };

  const openReport = (r: ReportDef) => {
    setActiveReport({ ...r, blocks: r.blocks.map((b) => ({ ...b, config: { ...b.config } })) });
    if (r.id) loadSignatures(r.id); else setSignatures([]);
  };

  const addBlock = (type: BlockType) => {
    if (!activeReport) return;
    setActiveReport({
      ...activeReport,
      blocks: [...activeReport.blocks, { id: newBlockId(), type, config: defaultConfigFor(type) }],
    });
  };

  const updateBlockConfig = (blockId: string, config: any) => {
    if (!activeReport) return;
    setActiveReport({
      ...activeReport,
      blocks: activeReport.blocks.map((b) => (b.id === blockId ? { ...b, config } : b)),
    });
  };

  const removeBlock = (blockId: string) => {
    if (!activeReport) return;
    setActiveReport({ ...activeReport, blocks: activeReport.blocks.filter((b) => b.id !== blockId) });
  };

  const moveBlock = (blockId: string, dir: -1 | 1) => {
    if (!activeReport) return;
    const idx = activeReport.blocks.findIndex((b) => b.id === blockId);
    const target = idx + dir;
    if (idx === -1 || target < 0 || target >= activeReport.blocks.length) return;
    const next = [...activeReport.blocks];
    [next[idx], next[target]] = [next[target], next[idx]];
    setActiveReport({ ...activeReport, blocks: next });
  };

  const saveReport = async () => {
    if (!activeReport) return;
    if (!activeReport.name.trim()) { toast.error('Report name is required'); return; }
    setSaving(true);
    try {
      if (activeReport.id) {
        const res = await fetch(`${API_BASE}/api/clients/${clientId}/custom-reports/${activeReport.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ name: activeReport.name, blocks: activeReport.blocks }),
        });
        if (!res.ok) throw new Error((await res.json())?.error || 'Failed to save');
      } else {
        const res = await fetch(`${API_BASE}/api/clients/${clientId}/custom-reports`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ name: activeReport.name, blocks: activeReport.blocks }),
        });
        const payload = await res.json();
        if (!res.ok || !payload?.success) throw new Error(payload?.error || 'Failed to save');
        setActiveReport({ ...activeReport, id: payload.id });
      }
      toast.success('Report saved');
      loadReports();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async (r: ReportDef) => {
    try {
      await fetch(`${API_BASE}/api/clients/${clientId}/custom-reports/${r.id}`, { method: 'DELETE', headers: authHeaders });
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      if (activeReport?.id === r.id) setActiveReport(null);
      toast.success('Report deleted');
    } catch (_) {
      toast.error('Failed to delete report');
    }
  };

  const generateReport = async (format: 'pdf' | 'xlsx') => {
    if (!activeReport?.id) { toast.error(`Save the report before generating a ${format.toUpperCase()}`); return; }
    setGenerating(true);
    try {
      const from = new Date(fromDate).toISOString();
      const to = new Date(`${toDate}T23:59:59`).toISOString();
      const res = await fetch(
        `${API_BASE}/api/clients/${clientId}/custom-reports/${activeReport.id}/${format}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        { headers: authHeaders }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `Failed to generate ${format.toUpperCase()}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeReport.name.replace(/[^a-z0-9]+/gi, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} generated`);
    } catch (err: any) {
      toast.error(err?.message || `Failed to generate ${format.toUpperCase()}`);
    } finally {
      setGenerating(false);
    }
  };

  const submitSignature = async () => {
    if (!activeReport?.id) return;
    if (!signPassword) { toast.error('Enter your password to sign'); return; }
    if (!signReason.trim()) { toast.error('Enter a reason for signing'); return; }
    setSigning(true);
    try {
      const from = new Date(fromDate).toISOString();
      const to = new Date(`${toDate}T23:59:59`).toISOString();
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/custom-reports/${activeReport.id}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ password: signPassword, reason: signReason.trim(), meaning: signMeaning, from, to }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || 'Failed to sign report');
      toast.success('Report signed and locked');
      setSignDialogOpen(false);
      setSignPassword('');
      setSignReason('');
      loadSignatures(activeReport.id);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to sign report');
    } finally {
      setSigning(false);
    }
  };

  const downloadSignedPdf = (sig: ReportSignature) => {
    if (!activeReport?.id) return;
    const url = `${API_BASE}/api/clients/${clientId}/custom-reports/${activeReport.id}/signatures/${sig.id}/pdf`;
    fetch(url, { headers: authHeaders })
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to download signed PDF');
        const blob = await res.blob();
        const objUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `signed_${sig.meaning.toLowerCase()}_${sig.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error('Failed to download signed PDF'));
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading report builder...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Custom Report Builder</h2>
        <p className="text-sm text-muted-foreground">
          Design your own PDF report from real, live data — pick the blocks, arrange them, then generate for any date range.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Left: saved reports + block palette */}
        <div className="space-y-6">
          <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm flex items-center gap-1.5"><FolderOpen className="h-4 w-4" /> Saved Reports</h3>
              <Button size="sm" variant="ghost" onClick={createNewReport}><Plus className="h-4 w-4" /></Button>
            </div>
            {reports.length === 0 && <p className="text-xs text-muted-foreground">No saved reports yet.</p>}
            {reports.map((r) => (
              <div
                key={r.id}
                className={`flex items-center justify-between gap-1 rounded-md px-2 py-1.5 text-sm cursor-pointer border ${
                  activeReport?.id === r.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted'
                }`}
                onClick={() => openReport(r)}
              >
                <span className="truncate flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{r.name}</span>
                <Button
                  size="icon" variant="ghost" className="h-6 w-6 shrink-0"
                  onClick={(e) => { e.stopPropagation(); deleteReport(r); }}
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          {activeReport && (
            <div className="border rounded-lg p-4 space-y-2">
              <h3 className="font-medium text-sm">Add Block</h3>
              {BLOCK_TYPES.map((bt) => (
                <button
                  key={bt.type}
                  onClick={() => addBlock(bt.type)}
                  className="w-full flex items-start gap-2 text-left rounded-md border px-2 py-2 text-xs hover:bg-muted transition-colors"
                >
                  <bt.icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <span>
                    <span className="block font-medium text-sm">{bt.label}</span>
                    <span className="block text-muted-foreground">{bt.description}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: canvas */}
        <div className="space-y-4">
          {!activeReport ? (
            <div className="border rounded-lg p-10 text-center text-sm text-muted-foreground">
              Select a saved report or click + to start a new one.
            </div>
          ) : (
            <>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] items-end">
                  <div>
                    <Label>Report Name</Label>
                    <Input value={activeReport.name} onChange={(e) => setActiveReport({ ...activeReport, name: e.target.value })} />
                  </div>
                  <Button onClick={saveReport} disabled={saving}>
                    <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-[auto_auto_auto_auto] items-end">
                  <div>
                    <Label>From</Label>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div>
                    <Label>To</Label>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                  <Button variant="outline" onClick={() => generateReport('pdf')} disabled={generating}>
                    <Download className="h-4 w-4 mr-1" /> {generating ? 'Generating...' : 'Generate PDF'}
                  </Button>
                  <Button variant="outline" onClick={() => generateReport('xlsx')} disabled={generating}>
                    <Download className="h-4 w-4 mr-1" /> {generating ? 'Generating...' : 'Generate Excel'}
                  </Button>
                </div>
                <div>
                  <Button
                    variant="outline"
                    onClick={() => setSignDialogOpen(true)}
                    disabled={!activeReport.id}
                    title={!activeReport.id ? 'Save the report first' : undefined}
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  >
                    <PenLine className="h-4 w-4 mr-1" /> Sign &amp; Lock
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Signs this exact PDF (for the From/To dates above) with your identity, a reason, and a locked, permanently stored copy — same idea as a 21 CFR Part 11 electronic signature.
                  </p>
                </div>

                {signatures.length > 0 && (
                  <div className="border-t pt-3 mt-1 space-y-2">
                    <p className="text-xs font-medium flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Signed Versions</p>
                    {signatures.map((sig) => (
                      <div key={sig.id} className="flex items-center justify-between gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5">
                        <span>
                          <span className="font-medium">{sig.meaning}</span> by {sig.signedByName || sig.signedByEmail}
                          {' · '}{new Date(sig.signedAt).toLocaleString()}
                          {' · '}"{sig.reason}"
                        </span>
                        <Button size="sm" variant="ghost" className="h-6 px-2 shrink-0" onClick={() => downloadSignedPdf(sig)}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {activeReport.blocks.length === 0 && (
                  <div className="border border-dashed rounded-lg p-8 text-center text-sm text-muted-foreground">
                    No blocks yet — add one from the left panel.
                  </div>
                )}
                {activeReport.blocks.map((block, idx) => {
                  const meta = BLOCK_TYPES.find((bt) => bt.type === block.type);
                  return (
                    <div key={block.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          {meta?.icon && <meta.icon className="h-4 w-4 text-muted-foreground" />}
                          {meta?.label || block.type}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={idx === 0} onClick={() => moveBlock(block.id, -1)}>
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={idx === activeReport.blocks.length - 1} onClick={() => moveBlock(block.id, 1)}>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeBlock(block.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      {block.type === 'heading' && (
                        <Input
                          value={block.config.text || ''}
                          placeholder="Heading text"
                          onChange={(e) => updateBlockConfig(block.id, { ...block.config, text: e.target.value })}
                        />
                      )}

                      {block.type === 'text' && (
                        <Textarea
                          value={block.config.text || ''}
                          placeholder="Free-form paragraph text"
                          onChange={(e) => updateBlockConfig(block.id, { ...block.config, text: e.target.value })}
                        />
                      )}

                      {block.type === 'kpi-row' && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {KPI_METRIC_OPTIONS.map((m) => {
                            const checked = (block.config.metrics || []).includes(m.key);
                            return (
                              <label key={m.key} className="flex items-center gap-2 text-xs">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    const current: string[] = block.config.metrics || [];
                                    const next = v ? [...current, m.key] : current.filter((k) => k !== m.key);
                                    updateBlockConfig(block.id, { ...block.config, metrics: next });
                                  }}
                                />
                                {m.label}
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {block.type === 'trend-table' && (
                        <div className="space-y-2">
                          <Input
                            value={block.config.title || ''}
                            placeholder="Table title"
                            onChange={(e) => updateBlockConfig(block.id, { ...block.config, title: e.target.value })}
                          />
                          <Select
                            value={block.config.plcId || undefined}
                            onValueChange={(v) => updateBlockConfig(block.id, { ...block.config, plcId: v, tagIds: [] })}
                          >
                            <SelectTrigger><SelectValue placeholder="Select PLC" /></SelectTrigger>
                            <SelectContent>
                              {plcOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {block.config.plcId && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border rounded-md p-2 max-h-32 overflow-auto">
                              {tagsForPlc(block.config.plcId).map((t) => {
                                const checked = (block.config.tagIds || []).includes(t.id);
                                return (
                                  <label key={t.id} className="flex items-center gap-2 text-xs">
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={(v) => {
                                        const current: string[] = block.config.tagIds || [];
                                        const next = v ? [...current, t.id] : current.filter((k) => k !== t.id);
                                        updateBlockConfig(block.id, { ...block.config, tagIds: next });
                                      }}
                                    />
                                    {t.name}
                                  </label>
                                );
                              })}
                              {tagsForPlc(block.config.plcId).length === 0 && (
                                <p className="text-xs text-muted-foreground col-span-full">No tags found for this PLC.</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {['energy-summary', 'alarm-summary', 'work-order-summary', 'asset-health', 'divider'].includes(block.type) && (
                        <p className="text-xs text-muted-foreground">{meta?.description} — no configuration needed, pulls live data for the selected date range automatically.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={signDialogOpen} onOpenChange={(open) => { setSignDialogOpen(open); if (!open) { setSignPassword(''); setSignReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenLine className="h-4 w-4" /> Sign &amp; Lock Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Generates and permanently stores this exact PDF (period {fromDate} to {toDate}) with your name, role, and timestamp stamped on it. This cannot be undone or deleted afterward.
            </p>
            <div>
              <Label>Meaning</Label>
              <Select value={signMeaning} onValueChange={setSignMeaning}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIGNATURE_MEANINGS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                value={signReason}
                onChange={(e) => setSignReason(e.target.value)}
                placeholder="Why are you signing this report?"
                rows={2}
              />
            </div>
            <div>
              <Label>Confirm your password</Label>
              <Input
                type="password"
                value={signPassword}
                onChange={(e) => setSignPassword(e.target.value)}
                placeholder="Your account password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialogOpen(false)} disabled={signing}>Cancel</Button>
            <Button onClick={submitSignature} disabled={signing}>
              {signing ? 'Signing...' : 'Sign & Lock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
